import { useCallback } from "react"
import { useSessionStorage } from "usehooks-ts"

import type { AssortmentTab } from "~/components/demand-2/assortment/useAssortmentSets"
import { presetStorageKey } from "~/components/planning/presetView"
import { useTableSearch } from "~/hooks/useTableSearch"

/** One spelling per key, so a jump from a grid writes the key the tab reads. */
export const demandPresetGroupId = (collectionId: number) => `demand2-${collectionId}-demand`

export const assortmentPresetGroupId = (collectionId: number) =>
  `demand2-${collectionId}-assortment`

export const preordersTableId = (collectionId: number) => `demand2-preorders-${collectionId}`

/**
 * Layouts are stored per table id, so bumping a version resets that view's saved columns to its
 * defaults. NOOS and fashion version apart, so a NOOS reset keeps fashion's layouts.
 */
const ASSORTMENT_TABLE_VERSION: Record<
  "fashion" | "noos",
  Partial<Record<AssortmentTab, string>>
> = {
  fashion: { new: "", carryover: "-v4", phasedOut: "-v3" },
  noos: { new: "-v2", live: "-v3", carryover: "-v5", phasedOut: "-v4" },
}

const ASSORTMENT_TABLE_NAME: Record<AssortmentTab, string> = {
  new: "new",
  live: "live",
  carryover: "carryover",
  phasedOut: "phased-out",
  hidden: "hidden",
}

/** One tableId per Assortment view, so column preferences stay scoped per view. */
export const assortmentTableId = (
  view: AssortmentTab,
  kind: "fashion" | "noos",
  collectionId: number,
) =>
  `demand2-assortment-${ASSORTMENT_TABLE_NAME[view]}${ASSORTMENT_TABLE_VERSION[kind][view] ?? ""}-${collectionId}`

/**
 * Opens the Pre-orders view narrowed to one variant, through the table's own search (not the page
 * filters, which Total shares). Quoted: the search is a substring match, so a bare id would also
 * catch every longer id it prefixes.
 */
export const useOpenInPreorders = (collectionId: number) => {
  const { setSearchText, setSearchBy } = useTableSearch(preordersTableId(collectionId))
  const [, setPresetId] = useSessionStorage<string | null>(
    presetStorageKey(demandPresetGroupId(collectionId)),
    null,
  )
  return useCallback(
    (variantId: string) => {
      setSearchBy("variant")
      setSearchText(`"${variantId}"`)
      setPresetId("preorders")
    },
    [setSearchBy, setSearchText, setPresetId],
  )
}
